import type { ListCriteriaTags, ListCriteriaV1 } from '@/types/models'

/**
 * Client-side mirror of the API's `ListCriteriaV1` rules (rivulet v0.14.0).
 *
 * The vocabulary is CLOSED: the server answers an unknown key, a newer `v`,
 * an unknown tag slug or feed id with a 400 and never silently ignores it.
 * These helpers keep the builder honest before the round-trip; the server
 * stays the only authority and its 400 text is surfaced verbatim.
 */

export const CRITERIA_SCHEMA_VERSION = 1

/** Keys the v1 document accepts. Anything else is a 400. */
export const CRITERIA_KEYS = ['v', 'feeds', 'tags', 'unread', 'favorites', 'max_age'] as const
export const CRITERIA_TAG_KEYS = ['any', 'all', 'none'] as const

export const TAG_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/
export const MAX_AGE_PATTERN = /^\d+[hd]$/

export const MAX_FEEDS = 200
export const MAX_TAGS_PER_CLAUSE = 50
export const MAX_AGE_DAYS = 365
export const MAX_AGE_HOURS = MAX_AGE_DAYS * 24

export type CriteriaTagClause = (typeof CRITERIA_TAG_KEYS)[number]

/** The widest possible list: every item. */
export function emptyCriteria(): ListCriteriaV1 {
  return { v: CRITERIA_SCHEMA_VERSION }
}

/** `max_age` in hours, or null when the string is not a valid window. */
export function maxAgeHours(value?: string | null): number | null {
  if (!value || !MAX_AGE_PATTERN.test(value)) return null
  const amount = Number(value.slice(0, -1))
  if (!Number.isFinite(amount)) return null
  return value.endsWith('d') ? amount * 24 : amount
}

function cleanSlugs(values?: string[]): string[] {
  if (!Array.isArray(values)) return []
  const seen = new Set<string>()
  for (const raw of values) {
    const slug = String(raw ?? '').trim().toLowerCase()
    if (slug) seen.add(slug)
  }
  return [...seen]
}

/**
 * Drop empty clauses so the stored document says only what the user asked
 * for. An absent clause and an empty one mean the same thing to the server,
 * but the short document is the one worth showing in the JSON disclosure.
 */
export function normalizeCriteria(input?: Partial<ListCriteriaV1> | null): ListCriteriaV1 {
  const source = input ?? {}
  const out: ListCriteriaV1 = { v: source.v ?? CRITERIA_SCHEMA_VERSION }

  const feeds = Array.isArray(source.feeds)
    ? [...new Set(source.feeds.filter(id => typeof id === 'string' && id.length > 0))]
    : []
  if (feeds.length > 0) out.feeds = feeds

  const tags: ListCriteriaTags = {}
  for (const clause of CRITERIA_TAG_KEYS) {
    const slugs = cleanSlugs(source.tags?.[clause])
    if (slugs.length > 0) tags[clause] = slugs
  }
  if (Object.keys(tags).length > 0) out.tags = tags

  // Absent and `false` are the same to the server; keep the document short.
  if (source.unread === true) out.unread = true
  if (source.favorites === true) out.favorites = true

  const maxAge = typeof source.max_age === 'string' ? source.max_age.trim() : ''
  if (maxAge) out.max_age = maxAge

  return out
}

/**
 * Validate against the same closed vocabulary the server enforces. Returns
 * human-readable problems; an empty array means "worth sending".
 */
export function validateCriteria(input?: Partial<ListCriteriaV1> | null): string[] {
  const errors: string[] = []
  const criteria = input ?? {}

  if (criteria.v !== CRITERIA_SCHEMA_VERSION) {
    errors.push(
      `criteria: schema version must be ${CRITERIA_SCHEMA_VERSION} (got ${JSON.stringify(criteria.v)})`
    )
  }

  for (const key of Object.keys(criteria)) {
    if (!(CRITERIA_KEYS as readonly string[]).includes(key)) {
      errors.push(`criteria: unknown key "${key}"`)
    }
  }

  if (criteria.feeds !== undefined) {
    if (!Array.isArray(criteria.feeds)) {
      errors.push('criteria: "feeds" must be a list of feed ids')
    } else if (criteria.feeds.length > MAX_FEEDS) {
      errors.push(`criteria: at most ${MAX_FEEDS} feeds (got ${criteria.feeds.length})`)
    }
  }

  if (criteria.tags !== undefined) {
    for (const key of Object.keys(criteria.tags ?? {})) {
      if (!(CRITERIA_TAG_KEYS as readonly string[]).includes(key)) {
        errors.push(`criteria: unknown key "tags.${key}"`)
      }
    }
    for (const clause of CRITERIA_TAG_KEYS) {
      const slugs = criteria.tags?.[clause]
      if (slugs === undefined) continue
      if (!Array.isArray(slugs)) {
        errors.push(`criteria: "tags.${clause}" must be a list of tag slugs`)
        continue
      }
      if (slugs.length > MAX_TAGS_PER_CLAUSE) {
        errors.push(`criteria: at most ${MAX_TAGS_PER_CLAUSE} tags in "tags.${clause}"`)
      }
      for (const slug of slugs) {
        if (typeof slug !== 'string' || !TAG_SLUG_PATTERN.test(slug)) {
          errors.push(`criteria: "${slug}" is not a valid tag slug (tags.${clause})`)
        }
      }
    }
  }

  for (const key of ['unread', 'favorites'] as const) {
    const value = criteria[key]
    if (value !== undefined && typeof value !== 'boolean') {
      errors.push(`criteria: "${key}" must be true or false`)
    }
  }

  if (criteria.max_age !== undefined) {
    if (typeof criteria.max_age !== 'string' || !MAX_AGE_PATTERN.test(criteria.max_age)) {
      errors.push('criteria: "max_age" must look like 24h or 7d')
    } else {
      const hours = maxAgeHours(criteria.max_age)
      if (hours === null || hours <= 0) {
        errors.push('criteria: "max_age" must be a positive window')
      } else if (hours > MAX_AGE_HOURS) {
        errors.push(`criteria: "max_age" is capped at ${MAX_AGE_DAYS}d`)
      }
    }
  }

  return errors
}

/** True when the document selects everything (no clause narrows anything). */
export function isWideOpen(criteria?: ListCriteriaV1 | null): boolean {
  const normalized = normalizeCriteria(criteria)
  return Object.keys(normalized).length === 1
}

/**
 * One-line human summary of what a list selects, for list rows and headers.
 * Every clause ANDs, so the parts are joined with a middot.
 */
export function describeCriteria(
  criteria?: ListCriteriaV1 | null,
  feedNames?: Map<string, string>
): string {
  const normalized = normalizeCriteria(criteria)
  const parts: string[] = []

  if (normalized.feeds?.length) {
    const names = normalized.feeds.map(id => feedNames?.get(id) ?? id)
    parts.push(
      names.length <= 2
        ? names.join(' + ')
        : `${names.length} feeds`
    )
  }
  if (normalized.tags?.any?.length) parts.push(`any of ${normalized.tags.any.join(', ')}`)
  if (normalized.tags?.all?.length) parts.push(`all of ${normalized.tags.all.join(', ')}`)
  if (normalized.tags?.none?.length) parts.push(`excluding ${normalized.tags.none.join(', ')}`)
  if (normalized.unread) parts.push('unread only')
  if (normalized.favorites) parts.push('favorites only')
  if (normalized.max_age) parts.push(`newer than ${normalized.max_age}`)

  return parts.length > 0 ? parts.join(' · ') : 'Every item'
}

/** Pretty JSON exactly as it will be sent, for the advanced disclosure. */
export function criteriaJson(criteria?: Partial<ListCriteriaV1> | null): string {
  return JSON.stringify(normalizeCriteria(criteria), null, 2)
}
